import { Box, Button, Container, Typography, styled, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { FormLabel } from "react-bootstrap";
import OtpInput from "react-otp-input";
import { Link, useLocation, useNavigate } from "react-router-dom";
import CircularProgress from "@mui/material/CircularProgress";
import { UrlConstant } from "../../commom/UrlConstant";
import Axios from "../../config/axios";
import { useAppDispatch } from "../../Redux/hooks";
import { login, logout } from "../../Redux/authSlice";
import Successpopup from "../common/SharedComponent/Successpopup";

function Otp() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const location = useLocation();

  const userEmail = location.state?.email || sessionStorage.getItem("otpVerify_email") || "";
  const userId = location.state?.id || sessionStorage.getItem("otpVerify_id") || "";

  // `email` = the last CONFIRMED email (what OTP was actually sent to / what's saved).
  // `currentEmail` = what's in the input box right now (may differ while editing).
  const [email, setEmail] = useState(userEmail);
  const [currentEmail, setCurrentEmail] = useState(userEmail);

  const [isEditingEmailState, setIsEditingEmailState] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);

  // OTP flow state
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState("");
  const [isLoading, setIsLoading] = useState(false); // sending/resending otp
  const [verifyingOtp, setVerifyingOtp] = useState(false); // verifying otp

  // Error Popup state
  const [showErrorPopup, setShowErrorPopup] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  const triggerError = (msg: string) => {
    setErrorMessage(msg);
    setShowErrorPopup(true);
  };

  const isEditingEmail = isEditingEmailState || !otpSent;

  useEffect(() => {
    if (!email) {
      navigate("/");
    }
  }, [email, navigate]);

  const isValidEmail = (value: string): boolean => {
    const emailRegex = /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/;
    return emailRegex.test(value);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setCurrentEmail(newEmail);

    if (newEmail && !isValidEmail(newEmail)) {
      setIsEmailValid(false);
    } else {
      setIsEmailValid(true);
    }
  };

  const handleEditEmail = () => {
    setIsEditingEmailState(true);
    setOtp("");
  };

  const handleCancelEditEmail = () => {
    setIsEditingEmailState(false);
    setCurrentEmail(email);
    setIsEmailValid(true);
    setOtp("");
  };

  const sendOtp = async (): Promise<boolean> => {
    if (!isValidEmail(currentEmail)) {
      triggerError("Please enter a valid email format.");
      return false;
    }
    setIsLoading(true);
    try {
      const response = await Axios.post(UrlConstant.Auth.ResendOTP, {
        email: currentEmail,
      });

      if (response?.data?.result === true) {
        setOtpSent(true);
        setIsEditingEmailState(false);
        setOtp("");
        return true;
      }

      const errMsg = response?.data?.msg || "Unable to send OTP. Please try again.";
      triggerError(errMsg);
      return false;
    } catch (error: any) {
      const errMsg =
        error?.response?.data?.msg || "Unable to send OTP. Please try again.";
      triggerError(errMsg);
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const handleSendOtp = async () => {
    await sendOtp();
  };

  const handleResendOtp = async () => {
    await sendOtp();
  };

  const handleSubmit = async () => {
    if (!otp || otp.length !== 6) {
      triggerError("Please enter a 6-digit OTP.");
      return;
    }

    const payLoad = {
      email: currentEmail,
      otp: otp,
    };

    setVerifyingOtp(true);
    try {
      const response = await Axios.post(UrlConstant.Auth.VerifyEmailByOTP, payLoad);
      const resData = response.data;

      if (resData.result === true) {
        if (resData.data) {
          const updatedUser = { ...resData.data, email: currentEmail };
          dispatch(login(updatedUser));
          localStorage.setItem("user", JSON.stringify(updatedUser));
          setEmail(currentEmail);

          sessionStorage.removeItem("otpVerify_email");
          sessionStorage.removeItem("otpVerify_id");
          setTimeout(() => {
            if (updatedUser?.isVerify === false) {
              navigate("/otp", { state: { email: updatedUser.email, id: updatedUser._id } });
            } else if (updatedUser?.acceptDisclosure === false) {
              navigate("/terms", { state: updatedUser });
            } else if (updatedUser?.IsPINCreated === false) {
              navigate("/createpin", { state: updatedUser });
            } else {
              navigate("/", { state: updatedUser });
            }
          }, 100);
        } else {
          dispatch(logout());
          navigate("/login");
        }
      } else {
        triggerError(resData.msg || "Verification failed");
      }
    } catch (error: any) {
      triggerError(error.response?.data?.msg || "An error occurred while verifying the OTP.");
    } finally {
      setVerifyingOtp(false);
    }
  };

  useEffect(() => {
    if (location.state?.email) {
      sessionStorage.setItem("otpVerify_email", location.state.email);
    }
    if (location.state?.id) {
      sessionStorage.setItem("otpVerify_id", location.state.id);
    }
  }, [location.state?.email, location.state?.id]);

  const isEmailChanged = currentEmail !== email;

  return (
    <Container
      maxWidth={false}
      className="container_new bg-black"
      sx={{ maxWidth: "100%", margin: "0px", minHeight: "100vh", display: "flex", alignItems: "center" }}
    >
      <RightSection className="RightSection">
        <Box width="100%" textAlign="left">
          <Typography
            className="Typography fw-700 heading"
            sx={{
              color: "#ffffff",
              fontSize: { xs: "24px", sm: "28px" },
              fontWeight: 700,
              mb: 1,
            }}
          >
            Verify Your Email
          </Typography>
          <Typography
            className="Typography text-secondary"
            variant="body1"
            sx={{
              color: "rgba(255, 255, 255, 0.7)",
              fontSize: "15px",
              mb: 4,
            }}
          >
            We’ll send a verification code to confirm your email address.
          </Typography>
        </Box>

        <Box
          className="otp-box"
          sx={{
            display: "flex",
            flexDirection: "column",
            width: "100%",
          }}
        >
          {!isEditingEmail && (
            <>
              <Typography
                className="Typography heading"
                variant="body1"
                sx={{ mb: 0.5, color: "white", fontWeight: 400, fontSize: "15px", textAlign: "left", width: "100%" }}
              >
                We have sent OTP on <span style={{ color: "#f2674a", fontWeight: 600 }}>{currentEmail}</span>
              </Typography>
              <Box sx={{ width: "100%", mb: 3, textAlign: "left" }}>
                <span
                  style={{ color: "#f2674a", fontSize: "14px", fontWeight: 500, cursor: "pointer" }}
                  onClick={handleEditEmail}
                >
                  Want to change Email?
                </span>
              </Box>
            </>
          )}

          {isEditingEmail && (
            <>
              <FormLabel
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '14px',
                  marginBottom: '8px',
                  display: 'block',
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                <span>
                  Email{" "}
                  <span style={{ color: "#fa0000", fontSize: "14px" }}>*</span>
                </span>
              </FormLabel>
              <Box display="flex" alignItems="center" width="100%" mb={2}>
                <Box sx={{ position: "relative", width: "100%" }}>
                  <TextField
                    value={currentEmail}
                    onChange={handleEmailChange}
                    disabled={isLoading}
                    fullWidth
                    variant="outlined"
                    className="text-secondary"
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderColor: "rgba(255, 255, 255, 0.2)",
                        "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
                        "&:hover fieldset": { borderColor: "#f2674a" },
                        "&.Mui-focused fieldset": { borderColor: "#f2674a" },
                        background: "transparent",
                        color: "white",
                        borderRadius: "50px",
                        paddingRight: "100px",
                      },
                      "& .MuiInputBase-input": {
                        color: "white",
                        fontSize: "16px",
                        WebkitTextFillColor: "white",
                        padding: "12.5px 14px",
                      },
                      "& .MuiInputBase-input.Mui-disabled": {
                        color: "white",
                        WebkitTextFillColor: "white",
                        opacity: 1,
                      },
                    }}
                  />
                  <Button
                    variant="text"
                    onClick={handleSendOtp}
                    disabled={!isEmailValid || !currentEmail || isLoading}
                    sx={{
                      position: "absolute",
                      right: 8,
                      top: "50%",
                      transform: "translateY(-50%)",
                      color: "#f2674a",
                      fontWeight: 700,
                      fontSize: "15px",
                      textTransform: "none",
                      minWidth: "auto",
                      padding: "6px 12px",
                      borderRadius: "8px",
                      background: "none",
                      "&:hover": {
                        background: "none",
                        color: "#d55a3f",
                      },
                      opacity: (!isEmailValid || !currentEmail || isLoading) ? 0.6 : 1,
                      cursor: (!isEmailValid || !currentEmail || isLoading) ? "not-allowed" : "pointer",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {isLoading ? <CircularProgress size={16} color="inherit" /> : "Send OTP"}
                  </Button>
                </Box>
              </Box>
              {isEmailChanged && (
                <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
                  <span
                    style={{
                      color: "#f2674a",
                      fontSize: "14px",
                      cursor: isLoading ? "not-allowed" : "pointer",
                      opacity: isLoading ? 0.6 : 1,
                    }}
                    onClick={() => { if (!isLoading) handleCancelEditEmail(); }}
                  >
                    Cancel
                  </span>
                </Box>
              )}
            </>
          )}

          {!isEditingEmail && (
            <Box sx={{ width: "100%" }}>
              <FormLabel
                style={{
                  color: 'rgba(255, 255, 255, 0.9)',
                  fontSize: '14px',
                  marginBottom: '12px',
                  display: 'block',
                  textAlign: 'left',
                  width: '100%',
                }}
              >
                <span>
                  OTP{" "}
                  <span style={{ color: "#fa0000", fontSize: "14px" }}>*</span>
                </span>
              </FormLabel>
              <OtpInput
                value={otp}
                onChange={(val: string) => {
                  if (/^\d{0,6}$/.test(val)) setOtp(val);
                }}
                numInputs={6}
                inputType="tel"
                shouldAutoFocus
                containerStyle={{
                  display: "flex",
                  width: "100%",
                  justifyContent: "space-between",
                  gap: "10px",
                }}
                inputStyle={{
                  width: "54px",
                  height: "54px",
                  borderRadius: "12px",
                  border: "1px solid rgba(255, 255, 255, 0.2)",
                  background: "transparent",
                  color: "white",
                  fontSize: "22px",
                  fontWeight: "bold",
                  textAlign: "center",
                  outline: "none",
                }}
                renderInput={(props) => (
                  <input
                    {...props}
                    className="otp"
                    onKeyDown={(e: React.KeyboardEvent) => {
                      if (e.key === "Enter") handleSubmit();
                      else if (!/[0-9]/.test(e.key) && e.key !== "Backspace") e.preventDefault();
                    }}
                  />
                )}
              />
              <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end', mt: 1.5, mb: 3 }}>
                <span
                  style={{
                    fontSize: "14px",
                    fontWeight: 500,
                    color: "#f2674a",
                    cursor: verifyingOtp || isLoading ? "not-allowed" : "pointer",
                  }}
                  onClick={() => {
                    if (!(verifyingOtp || isLoading)) handleResendOtp();
                  }}
                >
                  Resend OTP?
                </span>
              </Box>
              <Button
                variant="contained"
                fullWidth
                sx={{
                  fontSize: "16px",
                  fontWeight: 700,
                  paddingY: "12px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  backgroundColor: "#f2674a",
                  color: "#fff",
                  borderRadius: "30px",
                  boxShadow: "none",
                  textTransform: "none",
                  "&:hover": {
                    backgroundColor: "#d55a3f",
                    boxShadow: "none",
                  },
                  "&.Mui-disabled": {
                    backgroundColor: "rgba(242, 103, 74, 0.5)",
                    color: "rgba(255, 255, 255, 0.7)",
                  },
                }}
                onClick={handleSubmit}
                disabled={verifyingOtp || isLoading || otp.length !== 6}
              >
                {verifyingOtp ? (
                  <CircularProgress size={22} color="inherit" />
                ) : (
                  <>
                    Submit
                    <svg
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                      <path
                        d="M7.75 12L10.58 14.83L16.25 9.17"
                        stroke="white"
                        strokeWidth="1.5"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </>
                )}
              </Button>
            </Box>
          )}
        </Box>

        <Box sx={{ width: "100%", display: "flex", justifyContent: "flex-end", mt: 4 }}>
          <Typography sx={{ color: "rgba(255, 255, 255, 0.7)", fontSize: "14px" }}>
            Don't have an account?{" "}
            <Link to="/signup" style={{ color: "#f2674a", fontWeight: 600, textDecoration: "none" }}>
              Sign Up
            </Link>
          </Typography>
        </Box>
      </RightSection>

      {showErrorPopup && (
        <Successpopup
          Heading="Error"
          Success={false}
          onClose={() => setShowErrorPopup(false)}
          Message={errorMessage}
        />
      )}
    </Container>
  );
}

export default Otp;

const RightSection = styled(Box)(({ theme }) => ({
  width: "100%",
  maxWidth: "480px",
  margin: "auto",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "flex-start",
  padding: "40px 20px",
  minHeight: "100vh",
  [theme.breakpoints.down("md")]: {
    width: "100%",
    padding: "30px 16px",
  },
}));